import React from "react";
import { useState, useRef, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import MapContext from "../../MapContext";
import { Popup } from "@progress/kendo-react-popup";
import { FiAlignRight, FiMoreVertical } from "react-icons/fi";
import { inAndOut } from "ol/easing";
import { clearResults, removeLayer } from "../../../redux/resultsSlice";
import { setFeaturesToBuffer } from "../../../redux/identifySlice";
import { getFeatureWithId } from "../../utils/mapUtils";
import { Point } from "ol/geom.js";
import { setDataGrid } from "../../../redux/toolSelectSlice";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { filterReadFields, makeArray } from "../../../permissionUtils";
import { createGeocodeData } from "../../DataGrid/gridUtils";

// icon = "menu" || undefined
// results = selected results for one or more layer: redux -> resultsData -> results
const GroupPopup = ({ results, icon }) => {
    const [show, setShow] = useState(false);
    const [noExcelDataWarning, setNoExcelDataWarning] = useState(false);
    const popupAnchor = useRef();
    const popupRef = useRef();
    const { map } = useContext(MapContext);
    const geocodingData = useSelector((state) => state.resultsData.geocodingData);
    const dispatch = useDispatch();
    const dataGridOn = useSelector((state) => state.toolSelect.dataGrid);
    const vectorData = useSelector((state) => state.configData.vectorLayerProperties);
    const authPermissions = useSelector(
        (state) => state.authorizedPermissions.fieldPermissions
    );
    const _multiSheetExporter = useRef();

    const handlePopup = (e) => {
        e.stopPropagation();
        setShow(!show);
    };

    const handleSwitchView = () => {
        dispatch(setDataGrid(!dataGridOn));
    };

    const handleRemoveFeatures = () => {
        dispatch(removeLayer(results[0].Id));
        setShow(false);
    };

    const handleClearResults = () => {
        dispatch(clearResults([]));
    };

    const detectClick = (e) => {
        if (
            !popupRef.current.contains(e.target) &&
            !popupAnchor.current.contains(e.target)
        ) {
            setShow(false);
        }
    };

    const exportMultiSheetExcel = () => {
        if (_multiSheetExporter.current) {
            const data = createMultiSheetData(
                map,
                results,
                geocodingData,
                vectorData,
                authPermissions
            );
            if (data.length === 0) {
                setNoExcelDataWarning(true);
                return;
            }
            const options = _multiSheetExporter.current.workbookOptions();
            options.sheets = [];
            data.forEach((d) => options.sheets.push(d));
            _multiSheetExporter.current.save(options);
        }
    };

    useEffect(() => {
        if (show) {
            document.addEventListener("mousedown", detectClick);
        }
        return () => {
            document.removeEventListener("mousedown", detectClick);
        };
    }, [show]);

    const handleZoomToAllFeatures = () => {
        let extentOfFeatures;
        results.forEach((result) => {
            result.Features.forEach((feature) => {
                let extent;
                if (result.DisplayName === "ArcGIS Geocoder") {
                    let loc = geocodingData.candidates.find(
                        (data) => data.attributes.Match_addr === feature
                    );
                    const geom = new Point([loc.location.x, loc.location.y]);
                    extent = geom.getExtent();
                } else {
                    const feat = getFeatureWithId(map, result.Id, feature);
                    extent = feat.getGeometry().getExtent();
                }

                if (extentOfFeatures === undefined) {
                    // extentOfFeatures = extent was changing the extent for the map feature
                    extentOfFeatures = [...extent];
                } else {
                    if (extentOfFeatures[0] > extent[0]) {
                        extentOfFeatures.splice(0, 1, extent[0]);
                    }

                    if (extentOfFeatures[1] > extent[1]) {
                        extentOfFeatures.splice(1, 1, extent[1]);
                    }

                    if (extentOfFeatures[2] < extent[2]) {
                        extentOfFeatures.splice(2, 1, extent[2]);
                    }

                    if (extentOfFeatures[3] < extent[3]) {
                        extentOfFeatures.splice(3, 1, extent[3]);
                    }
                }
            });
        });
        map.getView().fit(extentOfFeatures, {
            padding: [60, 60, 60, 60],
            duration: 750,
            easing: inAndOut,
            minResolution: 1,
        });
        setShow(false);
    };

    const handleBufferOptions = () => {
        const bufferPayload = [];
        results.forEach((result) => {
            if (result.DisplayName === "ArcGIS Geocoder") {
                let featureCoords = [];
                result.Features.forEach((feature) => {
                    let loc = geocodingData.candidates.find(
                        (data) => data.attributes.Match_addr === feature
                    );
                    featureCoords.push([loc.location.x, loc.location.y]);
                });
                bufferPayload.push({
                    layerId: "ArcGIS Geocoder",
                    featureIds: featureCoords,
                });
            } else {
                let featuresToBuffer = [];
                featuresToBuffer.push({
                    layerId: result.Id,
                    featureIds: result.Features,
                });
                bufferPayload.push(...featuresToBuffer);
            }
        });
        dispatch(setFeaturesToBuffer(bufferPayload));
        setShow(false);
    };

    useEffect(() => {
        if (!noExcelDataWarning) {
            return;
        }
        const timer = setTimeout(() => setNoExcelDataWarning(false), 2000);
        return () => clearTimeout(timer);
    }, [noExcelDataWarning]);

    return (
        <>
            <div onClick={handlePopup} ref={popupAnchor} className="layer-options-button">
                {icon === "menu" ? <FiAlignRight /> : <FiMoreVertical />}
            </div>
            <Popup
                anchor={popupAnchor.current}
                show={show}
                className="layer-options-popup">
                <div ref={popupRef}>
                    <div className="popup-option" onClick={handleZoomToAllFeatures}>
                        {" "}
                        <div className="popup-label">Zoom to Features</div>
                        <div>Zoom to the extent of results</div>
                    </div>
                    <div className="popup-option" onClick={exportMultiSheetExcel}>
                        <div className="popup-label"> Export to Excel</div>
                        {noExcelDataWarning ? (
                            <div style={{ color: "red" }}>No data to export</div>
                        ) : (
                            <div>Download an .xlsx file</div>
                        )}
                    </div>
                    {results.length === 1 ? (
                        <div className="popup-option" onClick={handleRemoveFeatures}>
                            <div className="popup-label">Remove from Results</div>
                            <div>Remove features from results</div>
                        </div>
                    ) : null}
                    {results.length > 1 && icon === "menu" ? (
                        <div className="popup-option" onClick={handleClearResults}>
                            <div className="popup-label">Clear Results</div>
                            <div>Remove all features from results</div>
                        </div>
                    ) : null}
                    <div className="popup-option" onClick={handleBufferOptions}>
                        <div className="popup-label">Show Buffer Options</div>
                        <div>Identify features near these results</div>
                    </div>
                    {icon === "menu" && (
                        <div className="popup-option" onClick={handleSwitchView}>
                            <div className="popup-label">Switch View</div>
                            <div>Change to {dataGridOn ? "list" : "table"}</div>
                        </div>
                    )}
                </div>
            </Popup>
            <ExcelExport
                ref={_multiSheetExporter}
                fileName={
                    results.length === 1 ? `${results[0].DisplayName}.xlsx` : "Data.xlsx"
                }
            />
        </>
    );
};

export default GroupPopup;

/**
 * Takes an array of results data and generates a data set to be exported as an excel file
 * @param {Object} map Map object
 * @param {Array<Object>} results Results from redux
 * @param {Object} geocodingData Geocode results from redux
 * @param {Array<Object>} vectorData Vector metadata from redux -> configData.vectorLayerProperties
 * @param {Array<Object>} authorizedPermissions Field permissions from redux
 *
 * @return {Array<Object>} Array of objects representing excel sheets
 */
const createMultiSheetData = (
    map,
    results,
    geocodingData,
    vectorData,
    authPermissions
) => {
    const multiSheetArray = [];
    results.forEach((layer) => {
        if (layer.DisplayName === "ArcGIS Geocoder") {
            const [geoData, geoFields, geoSelected] = createGeocodeData(
                results[0],
                geocodingData
            );
            const fields = geoFields.map((f) => f.Name);
            const rows = [];
            rows.push({
                type: "header",
                cells: fields.map((f) => ({
                    background: "#7a7a7a",
                    color: "#fff",
                    value: f,
                })),
            });
            geoData.forEach((feature) => {
                const cells = Object.entries(feature)
                    .filter(([key, val]) => fields.includes(key))
                    .map(([key, val]) => ({ value: val }));

                rows.push({ type: "data", cells });
            });
            const columns = fields.map(() => ({ autoWidth: true }));
            const sheetObj = { title: "ArcGIS Geocoder", rows, columns };
            multiSheetArray.push(sheetObj);
        } else {
            // Match to vector metadata and get fields
            const matchedVectorData = vectorData.find((v) => v.Id === layer.Id);
            const fields = makeArray(matchedVectorData.Fields.Field);
            // Filter by authorized readability and by field visibility setting
            const filterFields = filterReadFields(fields, authPermissions, layer.Id)
                .filter((f) => f.Visible)
                .map((f) => f.Name);
            // If not data to export
            if (filterFields.length === 0) {
                return;
            }
            const rows = [];
            // Create header cells
            rows.push({
                type: "header",
                cells: filterFields.map((f) => ({
                    background: "#7a7a7a",
                    color: "#fff",
                    value: f,
                })),
            });
            // Get data from map, filter and push to rows
            layer.Features.forEach((feature) => {
                // Get data from map and separate geometry value
                const f = getFeatureWithId(map, layer.Id, feature);
                const { geometry, ...data } = f.values_;
                // Turn object into entries and filter using filterFields
                const cells = Object.entries(data)
                    .filter(([key, val]) => filterFields.includes(key))
                    .map(([key, val]) => ({ value: val }));

                rows.push({ type: "data", cells: cells });
            });
            const columns = filterFields.map(() => ({ autoWidth: true }));
            const sheetObj = { title: layer.DisplayName, rows, columns };
            multiSheetArray.push(sheetObj);
        }
    });
    return multiSheetArray;
};
